import { Field, Input, Label } from '@zendeskgarden/react-forms'
import { capitalize } from 'lodash'
import React, { useCallback, useState } from 'react'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import LeftArrow from '../../icons/left-arrow.svg?react'
import { PSLink } from '../../models/PSLink'
import { RecordFactory } from '../../models/RecordFactory'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { ComponentData } from '../../utils/typing'
import { NavigationBlockerPrompt } from '../NavigationBlockerPrompt'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import { PSButton, PSButtonDanger, PSButtonPrimary } from '../app/PSButton'
import { DangerModal } from '../common/modals/DangerModal'

type UrlParams = {
    linkID?: string
}

type LocationStateType = {
    linkData?: { type: string; label: string }
}

const viewWillAppear = async (base: PocketComponent) => {
    // favor url param link if present
    const urlParams = base.urlParams as UrlParams | undefined

    if (urlParams?.linkID) {
        const link = await RecordFactory.getInstance().loadLink(urlParams.linkID)
        if (!link) {
            return new RecordNotFoundError()
        }

        return { link }
    }

    const locationState = base.locationState as LocationStateType | undefined

    const newLinkLabel = locationState?.linkData?.label

    // URLS for non-custom links are generated server-side
    // In the case where a user has deleted a default link but now wishes to add it back, we need to display something
    // for the URL which has not yet been generated on the server. We are using the link type
    const newLinkURL =
        locationState?.linkData?.type !== 'custom'
            ? capitalize(locationState?.linkData?.type)
            : undefined

    return {
        link: new PSLink({
            label: newLinkLabel,
            url: newLinkURL,
            type: locationState?.linkData?.type ?? 'custom',
        }),
    }
}

const render = ({ data, base }: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const navigate = usePSNavigate()

    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
    const [labelText, setLabelText] = useState(data.link.label)
    const [linkURL, setLinkURL] = useState(data.link.url)

    const isExistingLink = Boolean(data.link.id)

    // we cannot edit urls for default links, only custom links allow user-defined URLs
    const isDefaultSuiteLink = data.link.type !== 'custom'

    const hasEdits = !data.link.id || data.link?.label !== labelText || data.link?.url !== linkURL

    const onSave = async () => {
        if (!hasEdits) {
            return
        }

        const linkToSave = data.link.clone()
        if (!linkToSave) {
            return
        }

        linkToSave.label = labelText
        linkToSave.url = linkURL // this value is ignored if the type is non custom

        base.showProgressBar()
        const result = await RecordFactory.getInstance().sendLink(linkToSave)
        base.hideProgressBar()

        if (!result.ok) {
            base.logAndDisplayError(result.error)
            return
        }

        navigate('/settings/link')
        return
    }

    const onDelete = useCallback(async () => {
        // should never happen, but we can only delete an item which has been created
        if (!data.link?.id) {
            return
        }

        base.showProgressBar()
        const result = await RecordFactory.getInstance().deleteLink(data.link.id)
        base.hideProgressBar()

        if (!result.ok) {
            base.logAndDisplayError(result.error)
            return
        }

        navigate('/settings/link')
    }, [data.link?.id])

    return (
        <div className="servicesSettings">
            <div className="header">
                <PSButton
                    style={{ marginLeft: 0, marginRight: 16 }}
                    onClick={() => navigate('/settings/link')}
                >
                    <LeftArrow />
                </PSButton>
                <h1>Button</h1>
                {isExistingLink && (
                    <PSButtonDanger
                        style={{ height: '40px' }}
                        onClick={() => setShowDeleteConfirmation(true)}
                    >
                        Delete
                    </PSButtonDanger>
                )}

                <PSButtonPrimary
                    style={{ height: '40px', marginLeft: isExistingLink ? '16px' : 'auto' }}
                    onClick={() => onSave()}
                    disabled={!hasEdits || !linkURL || !labelText}
                >
                    Save
                </PSButtonPrimary>
            </div>

            <div className="form">
                <Field className="field">
                    <Label className="label">Label (required)</Label>
                    <Input
                        type="text"
                        value={labelText}
                        onChange={(e) => setLabelText(e.target.value)}
                        placeholder="Enter text"
                    />
                </Field>
                <Field className="field">
                    <Label className="label">
                        {isDefaultSuiteLink ? 'Link (Generated by PocketSuite)' : 'Link (Required)'}
                    </Label>
                    <Input
                        type="text"
                        value={linkURL}
                        onChange={(e) => setLinkURL(e.target.value)}
                        placeholder="Enter text"
                        disabled={isDefaultSuiteLink}
                    />
                </Field>
            </div>

            {showDeleteConfirmation && (
                <DangerModal
                    title="Confirm change"
                    body="Confirm that you'd like to delete this suite link button."
                    buttonText="Delete"
                    disableDismiss={true}
                    onClose={() => {
                        setShowDeleteConfirmation(false)
                    }}
                    onAction={() => onDelete()}
                />
            )}
            <NavigationBlockerPrompt when={hasEdits} />
        </div>
    )
}

export const LinkPage = usePSComponent(render, viewWillAppear)
